// src/api/wpAuth.js

const AUTH_KEY = "wp_jwt";
const REFRESH_KEY = "wp_refresh";
const USER_KEY = "wp_user"; // cached viewer user

/*
 * Independent session information for this browser profile.
 *
 * Different browsers and devices use separate localStorage areas,
 * so their sessions do not overwrite or log out one another.
 */
const SESSION_ID_KEY =
  "wp_session_id";

const SESSION_EXPIRES_AT_KEY =
  "wp_session_expires_at";

const SESSION_KEEP_LOGIN_KEY =
  "wp_session_keep_login";

const AUTH_EVENT =
  "seho:wpAuthChanged";

/*
 * Incremented whenever login or logout starts.
 *
 * Asynchronous work from an older authentication action must not
 * save tokens or clear a newer browser session.
 */
let authSessionGeneration = 0;

/* =========================================================
   GraphQL docs
========================================================= */

/*
 * Authenticate the user and create one independent browser session.
 *
 * The keepLogin value from this login attempt determines whether the
 * fixed session deadline is 24 hours or 31 days.
 */
const LOGIN_MUTATION = `
mutation SehoLoginCustomer(
  $username: String!
  $password: String!
  $keepLogin: Boolean!
) {
  sehoLoginCustomer(
    input: {
      username: $username
      password: $password
      keepLogin: $keepLogin
    }
  ) {
    ok
    message
    authToken
    refreshToken
    sessionId
    sessionExpiresAt
    keepLogin
    user {
      id
      username
      email
      name
      firstName
      lastName
      roles {
        nodes {
          name
        }
      }
      sehoUserID
      phoneNumber
      landlinePhoneNumber
      address
      birthday
      gender
    }
  }
}
`;

/**
 * NOTE:
 * Your backend expects: refreshJwtAuthToken(input: { jwtRefreshToken: $refreshToken })
 * Keep as-is (matches your current schema usage).
 */
const REFRESH_MUTATION = `
mutation RefreshAuthToken($refreshToken: String!) {
  refreshJwtAuthToken(input: { jwtRefreshToken: $refreshToken }) {
    authToken
  }
}
`;

const ME_QUERY = `
query Me {
  viewer {
    id
    username
    email
    name
    firstName
    lastName
    roles { nodes { name } }
    sehoUserID
    phoneNumber
    landlinePhoneNumber
    address
    birthday
    gender
  }
}
`;

// ✅ Email OTP mutations
const REQUEST_EMAIL_OTP_MUTATION = `
mutation RequestEmailOtp($purpose: String!, $email: String, $lang: String) {
  sehoRequestEmailOtp(input: { purpose: $purpose, email: $email, lang: $lang }) {
    ok
    message
    expiresIn
  }
}
`;

const VERIFY_EMAIL_OTP_MUTATION = `
mutation VerifyEmailOtp($purpose: String!, $email: String, $code: String!) {
  sehoVerifyEmailOtp(input: { purpose: $purpose, email: $email, code: $code }) {
    ok
    message
  }
}
`;

/* =========================================================
   ✅ NEW: Forgot / Reset mutations
========================================================= */

const FORGOT_USERNAME_MUTATION = `
mutation ForgotUsername($email: String!, $lang: String) {
  sehoForgotUsername(input: { email: $email, lang: $lang }) {
    ok
    message
  }
}
`;

const FORGOT_PASSWORD_MUTATION = `
mutation ForgotPassword($email: String!, $lang: String) {
  sehoForgotPassword(input: { email: $email, lang: $lang }) {
    ok
    message
    expiresIn
  }
}
`;

const RESET_PASSWORD_WITH_TOKEN_MUTATION = `
mutation ResetPasswordWithToken($token: String!, $newPassword: String!) {
  sehoResetPasswordWithToken(input: { token: $token, newPassword: $newPassword }) {
    ok
    message
  }
}
`;

// ✅ NEW: validate reset token (frontend can redirect to NotFound if invalid/expired/used)
const VALIDATE_RESET_PASSWORD_TOKEN_MUTATION = `
mutation ValidateResetPasswordToken($token: String!) {
  sehoValidateResetPasswordToken(input: { token: $token }) {
    ok
    message
  }
}
`;

/* =========================================================
   ✅ NEW: Register / Profile mutations
========================================================= */

const REGISTER_MUTATION = `
mutation Register(
  $username: String!
  $email: String
  $password: String!
  $firstName: String
  $lastName: String
  $phoneNumber: String
  $keepLogin: Boolean
  $emailOtp: String
) {
  sehoRegisterCustomer(
    input: {
      username: $username
      email: $email
      password: $password
      firstName: $firstName
      lastName: $lastName
      phoneNumber: $phoneNumber
      keepLogin: $keepLogin
      emailOtp: $emailOtp
    }
  ) {
    ok
    message
    user {
      id
      username
      email
      name
      firstName
      lastName
      roles { nodes { name } }
      sehoUserID
      phoneNumber
      landlinePhoneNumber
      address
      birthday
      gender
    }
  }
}
`;

const UPDATE_PROFILE_MUTATION = `
mutation UpdateMyProfile(
  $email: String
  $firstName: String
  $lastName: String
  $phoneNumber: String
  $landlinePhoneNumber: String
  $address: String
  $birthday: String
  $gender: String
  $emailOtp: String
) {
  sehoUpdateMyProfile(
    input: {
      email: $email
      firstName: $firstName
      lastName: $lastName
      phoneNumber: $phoneNumber
      landlinePhoneNumber: $landlinePhoneNumber
      address: $address
      birthday: $birthday
      gender: $gender
      emailOtp: $emailOtp
    }
  ) {
    ok
    message
    user {
      id
      username
      email
      name
      firstName
      lastName
      roles { nodes { name } }
      sehoUserID
      phoneNumber
      landlinePhoneNumber
      address
      birthday
      gender
    }
  }
}
`;

/* =========================================================
   LocalStorage safe helpers
========================================================= */

function safeGet(key) {
  try {
    return localStorage.getItem(key) || "";
  } catch (e) {
    return "";
  }
}

function safeSet(key, val) {
  try {
    if (!val) localStorage.removeItem(key);
    else localStorage.setItem(key, String(val));
  } catch (e) {
    // ignore
  }
}

function safeSetJson(key, obj) {
  try {
    if (obj === undefined) return;
    if (obj === null) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(obj));
  } catch (e) {
    // ignore
  }
}

function safeGetJson(key) {
  try {
    const raw =
      localStorage.getItem(key);

    if (!raw) return null;

    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}

/**
 * Clear only the authentication session stored in this browser.
 *
 * This does not call the backend and does not invalidate sessions
 * belonging to another browser, device, or browser profile.
 */
function clearLocalAuthSessionStorage() {
  safeSet(AUTH_KEY, "");
  safeSet(REFRESH_KEY, "");
  safeSetJson(USER_KEY, null);

  safeSet(SESSION_ID_KEY, "");
  safeSet(
    SESSION_EXPIRES_AT_KEY,
    "",
  );
  safeSet(
    SESSION_KEEP_LOGIN_KEY,
    "",
  );
}

/* =========================================================
   Browser-session helpers
========================================================= */

/**
 * Accept only the session-ID format generated by the backend.
 */
function normalizeAuthSessionId(value) {
  const sessionId = String(
    value || "",
  ).trim();

  if (
    !/^[A-Za-z0-9_-]{32,128}$/.test(
      sessionId,
    )
  ) {
    return "";
  }

  return sessionId;
}

/**
 * Normalize a Unix timestamp to milliseconds.
 *
 * The backend returns sessionExpiresAt in seconds, while the
 * frontend stores and compares the value in milliseconds.
 */
function normalizeAuthSessionExpiresAtMs(
  value,
) {
  const numericValue = Number(value);

  if (
    !Number.isFinite(numericValue) ||
    numericValue <= 0
  ) {
    return null;
  }

  /*
   * Current Unix timestamps:
   * - seconds: approximately 1,700,000,000
   * - milliseconds: approximately 1,700,000,000,000
   */
  const milliseconds =
    numericValue < 100000000000
      ? numericValue * 1000
      : numericValue;

  const normalized =
    Math.trunc(milliseconds);

  if (
    !Number.isSafeInteger(normalized) ||
    normalized <= 0
  ) {
    return null;
  }

  return normalized;
}

/**
 * Normalize the current login's 保持登入 selection.
 *
 * Return null when the stored value is missing or invalid so an
 * incomplete previous session cannot silently become a valid one.
 */
function normalizeAuthSessionKeepLogin(
  value,
) {
  if (
    value === true ||
    value === 1 ||
    value === "1" ||
    value === "true"
  ) {
    return true;
  }

  if (
    value === false ||
    value === 0 ||
    value === "0" ||
    value === "false"
  ) {
    return false;
  }

  return null;
}

/**
 * Read the complete authentication session for this browser.
 *
 * Returns null when even one required session value is missing
 * or invalid.
 */
export function wpGetAuthSession() {
  const sessionId =
    normalizeAuthSessionId(
      safeGet(SESSION_ID_KEY),
    );

  const expiresAtMs =
    normalizeAuthSessionExpiresAtMs(
      safeGet(
        SESSION_EXPIRES_AT_KEY,
      ),
    );

  const keepLogin =
    normalizeAuthSessionKeepLogin(
      safeGet(
        SESSION_KEEP_LOGIN_KEY,
      ),
    );

  if (
    !sessionId ||
    !expiresAtMs ||
    keepLogin === null
  ) {
    return null;
  }

  return {
    sessionId,
    expiresAtMs,
    keepLogin,
  };
}

/**
 * Treat a missing or incomplete browser session as expired.
 */
export function wpIsAuthSessionExpired(
  sessionMaybe,
) {
  const session =
    sessionMaybe &&
    typeof sessionMaybe === "object"
      ? sessionMaybe
      : wpGetAuthSession();

  const expiresAtMs =
    normalizeAuthSessionExpiresAtMs(
      session?.expiresAtMs,
    );

  if (!expiresAtMs) {
    return true;
  }

  return Date.now() >= expiresAtMs;
}

/**
 * Check whether an asynchronous operation still belongs to the
 * browser session that originally started it.
 */
export function wpIsCurrentAuthSession(
  sessionId,
) {
  const expectedSessionId =
    normalizeAuthSessionId(sessionId);

  if (!expectedSessionId) {
    return false;
  }

  const currentSession =
    wpGetAuthSession();

  return (
    currentSession?.sessionId ===
    expectedSessionId
  );
}

/**
 * Save one complete browser session.
 *
 * The values are written only after every required field has passed
 * validation. A partial session is never intentionally stored.
 */
function saveLocalAuthSession({
  sessionId,
  sessionExpiresAt,
  keepLogin,
} = {}) {
  const normalizedSessionId =
    normalizeAuthSessionId(sessionId);

  const expiresAtMs =
    normalizeAuthSessionExpiresAtMs(
      sessionExpiresAt,
    );

  const normalizedKeepLogin =
    normalizeAuthSessionKeepLogin(
      keepLogin,
    );

  if (!normalizedSessionId) {
    throw new Error(
      "Invalid authentication session ID",
    );
  }

  if (!expiresAtMs) {
    throw new Error(
      "Invalid authentication session expiration",
    );
  }

  if (
    normalizedKeepLogin === null
  ) {
    throw new Error(
      "Invalid authentication keep-login mode",
    );
  }

  if (Date.now() >= expiresAtMs) {
    throw new Error(
      "Authentication session is already expired",
    );
  }

  safeSet(
    SESSION_ID_KEY,
    normalizedSessionId,
  );

  safeSet(
    SESSION_EXPIRES_AT_KEY,
    String(expiresAtMs),
  );

  safeSet(
    SESSION_KEEP_LOGIN_KEY,
    normalizedKeepLogin
      ? "1"
      : "0",
  );

  return {
    sessionId:
      normalizedSessionId,

    expiresAtMs,

    keepLogin:
      normalizedKeepLogin,
  };
}

/* =========================================================
   Token helpers
========================================================= */

function stripBearer(token) {
  const t = String(token || "").trim();
  if (!t) return "";
  return t.toLowerCase().startsWith("bearer ") ? t.slice(7).trim() : t;
}

function notifyAuthChanged(payload = {}) {
  try {
    window.dispatchEvent(new CustomEvent(AUTH_EVENT, { detail: payload }));
  } catch (e) {
    // ignore
  }
}

/**
 * Base64URL decode for JWT payload (supports unicode safely).
 */
function b64UrlDecodeToString(b64url) {
  try {
    const b64 = String(b64url || "")
      .replace(/-/g, "+")
      .replace(/_/g, "/");
    const pad = b64.length % 4 ? "=".repeat(4 - (b64.length % 4)) : "";
    const raw = atob(b64 + pad);
    return decodeURIComponent(
      raw
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );
  } catch (e) {
    return "";
  }
}

function decodeJwtPayload(token) {
  try {
    const t = stripBearer(token);
    if (!t) return null;

    const parts = t.split(".");
    if (parts.length < 2) return null;

    const jsonStr = b64UrlDecodeToString(parts[1]);
    if (!jsonStr) return null;

    const obj = JSON.parse(jsonStr);
    return obj && typeof obj === "object" ? obj : null;
  } catch (e) {
    return null;
  }
}

/**
 * Read and validate the signed SEHO browser-session claims
 * contained in an access token or refresh token.
 */
function readJwtAuthSessionClaims(
  tokenMaybe,
) {
  const token =
    stripBearer(tokenMaybe);

  if (!token) {
    return null;
  }

  const payload =
    decodeJwtPayload(token);

  if (
    !payload ||
    typeof payload !== "object"
  ) {
    return null;
  }

  const sessionId =
    normalizeAuthSessionId(
      payload.seho_session_id,
    );

  const issuedAtMs =
    normalizeAuthSessionExpiresAtMs(
      payload.seho_session_issued_at,
    );

  const expiresAtMs =
    normalizeAuthSessionExpiresAtMs(
      payload.seho_session_expires_at,
    );

  const keepLogin =
    normalizeAuthSessionKeepLogin(
      payload.seho_keep_login,
    );

  const tokenExpiresAtMs =
    normalizeAuthSessionExpiresAtMs(
      payload.exp,
    );

  if (
    !sessionId ||
    !issuedAtMs ||
    !expiresAtMs ||
    keepLogin === null ||
    !tokenExpiresAtMs
  ) {
    return null;
  }

  if (
    expiresAtMs <= issuedAtMs ||
    tokenExpiresAtMs !== expiresAtMs
  ) {
    return null;
  }

  return {
    sessionId,
    issuedAtMs,
    expiresAtMs,
    keepLogin,
  };
}

/**
 * Confirm that two tokens belong to the same independent
 * browser session.
 */
function authSessionClaimsMatch(
  first,
  second,
) {
  if (!first || !second) {
    return false;
  }

  return (
    first.sessionId ===
      second.sessionId &&
    first.issuedAtMs ===
      second.issuedAtMs &&
    first.expiresAtMs ===
      second.expiresAtMs &&
    first.keepLogin ===
      second.keepLogin
  );
}

/**
 * Validate a newly issued access-token and refresh-token pair.
 *
 * The token pair must:
 * - contain signed SEHO session claims;
 * - belong to the same browser session;
 * - use the fixed deadline returned by the login mutation;
 * - match the current 保持登入 selection;
 * - still be unexpired.
 */
function validateNewAuthTokenPair({
  authToken,
  refreshToken,
  expectedSessionId,
  expectedSessionExpiresAt,
  expectedKeepLogin,
} = {}) {
  const normalizedAuthToken =
    stripBearer(authToken);

  const normalizedRefreshToken =
    stripBearer(refreshToken);

  if (
    !normalizedAuthToken ||
    !normalizedRefreshToken
  ) {
    throw new Error(
      "Authentication tokens are missing",
    );
  }

  const authClaims =
    readJwtAuthSessionClaims(
      normalizedAuthToken,
    );

  const refreshClaims =
    readJwtAuthSessionClaims(
      normalizedRefreshToken,
    );

  if (
    !authClaims ||
    !refreshClaims
  ) {
    throw new Error(
      "Authentication tokens contain invalid session information",
    );
  }

  if (
    !authSessionClaimsMatch(
      authClaims,
      refreshClaims,
    )
  ) {
    throw new Error(
      "Authentication tokens belong to different sessions",
    );
  }

  const sessionId =
    normalizeAuthSessionId(
      expectedSessionId,
    );

  const expiresAtMs =
    normalizeAuthSessionExpiresAtMs(
      expectedSessionExpiresAt,
    );

  const keepLogin =
    normalizeAuthSessionKeepLogin(
      expectedKeepLogin,
    );

  if (
    !sessionId ||
    !expiresAtMs ||
    keepLogin === null
  ) {
    throw new Error(
      "Login response contains invalid session information",
    );
  }

  if (
    authClaims.sessionId !==
      sessionId ||
    authClaims.expiresAtMs !==
      expiresAtMs ||
    authClaims.keepLogin !==
      keepLogin
  ) {
    throw new Error(
      "Login response does not match the signed token session",
    );
  }

  if (
    Date.now() >=
    authClaims.expiresAtMs
  ) {
    throw new Error(
      "Authentication session is already expired",
    );
  }

  return {
    authToken:
      normalizedAuthToken,

    refreshToken:
      normalizedRefreshToken,

    sessionId:
      authClaims.sessionId,

    issuedAtMs:
      authClaims.issuedAtMs,

    expiresAtMs:
      authClaims.expiresAtMs,

    keepLogin:
      authClaims.keepLogin,
  };
}

/**
 * Validate a refreshed access token against the browser session
 * that requested the refresh.
 */
function validateRefreshedAuthToken(
  authToken,
  expectedSession,
) {
  const normalizedAuthToken =
    stripBearer(authToken);

  const claims =
    readJwtAuthSessionClaims(
      normalizedAuthToken,
    );

  if (
    !normalizedAuthToken ||
    !claims
  ) {
    throw new Error(
      "Refreshed authentication token is invalid",
    );
  }

  if (
    !expectedSession ||
    claims.sessionId !==
      expectedSession.sessionId ||
    claims.expiresAtMs !==
      expectedSession.expiresAtMs ||
    claims.keepLogin !==
      expectedSession.keepLogin
  ) {
    throw new Error(
      "Refreshed token does not match the current browser session",
    );
  }

  if (
    Date.now() >=
    claims.expiresAtMs
  ) {
    throw new Error(
      "Authentication session is expired",
    );
  }

  return normalizedAuthToken;
}

/* =========================================================
   ✅ helpers for birthday/gender normalization
========================================================= */

function normalizeGender(g) {
  const s = String(g || "")
    .toLowerCase()
    .trim();
  if (s === "male" || s === "female") return s;
  return "";
}

function normalizeBirthday(b) {
  const s = String(b || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return "";
  return s;
}

function compactUserForCache(user) {
  if (!user || typeof user !== "object") return user;
  return {
    ...user,
    birthday: user?.birthday ?? null,
    gender: user?.gender ?? null,
  };
}

/* =========================================================
   Public APIs: token + user cache
========================================================= */

export function wpGetTokens() {
  return {
    authToken: safeGet(AUTH_KEY),
    refreshToken: safeGet(REFRESH_KEY),
  };
}

export function wpHasAuthToken() {
  return !!stripBearer(safeGet(AUTH_KEY));
}

export function wpGetCachedUser() {
  return safeGetJson(USER_KEY);
}

export function wpSetCachedUser(user, meta = {}) {
  safeSetJson(USER_KEY, user ? compactUserForCache(user) : null);
  notifyAuthChanged({ user: user ? compactUserForCache(user) : null, ...meta });
}


export function wpLogout(meta = {}) {
  const requestedSessionId =
    normalizeAuthSessionId(
      meta?.sessionId,
    );

  /*
   * When the caller identifies a session, clear storage only when
   * that session is still the active session in this browser.
   *
   * This prevents delayed requests, refreshes, and expiry timers from
   * Session A from clearing a newer Session B.
   */
  if (
    requestedSessionId &&
    !wpIsCurrentAuthSession(
      requestedSessionId,
    )
  ) {
    return false;
  }

  /*
   * Invalidate any login or refresh operation that started before
   * this logout.
   */
  authSessionGeneration += 1;

  clearLocalAuthSessionStorage();

  notifyAuthChanged({
    user: null,
    hasToken: false,
    logout: true,

    reason:
      meta?.reason || "logout",

    message:
      meta?.message || "",

    sessionId:
      requestedSessionId || "",
  });

  return true;
}

export async function wpLogoutRemote({ debug = false } = {}) {
  try {
    if (debug)
      console.log("[wpLogoutRemote] no server logout mutation; clearing local");
  } catch (_) {
    // ignore
  } finally {
    wpLogout({ reason: "logout" });
  }
}

/**
 * After we store token, confirm by querying viewer.
 */
export async function wpFetchMe({
  debug = false,
} = {}) {
  const token = stripBearer(
    safeGet(AUTH_KEY),
  );

  const authSession =
    wpGetAuthSession();

  if (!token) {
    wpSetCachedUser(null, {
      reason: "no_token",
    });

    throw new Error(
      "Missing authentication token",
    );
  }

  if (!authSession) {
    throw new Error(
      "Missing authentication session",
    );
  }

  if (
    wpIsAuthSessionExpired(
      authSession,
    )
  ) {
    throw new Error(
      "Authentication session is expired",
    );
  }

  /*
   * Confirm that the stored access token still belongs to the
   * browser session stored alongside it.
   */
  const tokenClaims =
    readJwtAuthSessionClaims(token);

  if (
    !tokenClaims ||
    tokenClaims.sessionId !==
      authSession.sessionId ||
    tokenClaims.expiresAtMs !==
      authSession.expiresAtMs ||
    tokenClaims.keepLogin !==
      authSession.keepLogin
  ) {
    throw new Error(
      "Authentication token does not match the current browser session",
    );
  }

  const { wpGraphql } =
    await import("./wpGraphql");

  const data = await wpGraphql(
    ME_QUERY,
    {},
    {
      requestTag: "me",
      operationName: "Me",
      retryOnAuth: true,
      debug,
    },
  );

  const viewer = data?.viewer || null;

  wpSetCachedUser(viewer);

  if (!viewer?.id) {
    throw new Error("viewer is null");
  }

  return viewer;
}

/* =========================================================
   Email OTP APIs
========================================================= */

const OTP_PURPOSES = new Set([
  "register",
  "change_email",
  "change_password",
  "edit_profile",
]);

function otpPurposeNeedsAuth(purpose) {
  const p = String(purpose || "")
    .toLowerCase()
    .trim();
  return (
    p === "change_email" || p === "change_password" || p === "edit_profile"
  );
}

function otpPurposeUsesMeEmail(purpose) {
  const p = String(purpose || "")
    .toLowerCase()
    .trim();
  return p === "change_password" || p === "edit_profile";
}

async function getAuthedUserEmail({ debug = false } = {}) {
  const cached = wpGetCachedUser();
  const cachedEmail = String(cached?.email || "").trim();
  if (cachedEmail) return cachedEmail;

  try {
    const me = await wpFetchMe({ debug });
    return String(me?.email || "").trim();
  } catch (_) {
    return "";
  }
}

export async function wpRequestEmailOtp(
  { purpose, email, lang = "ZH" },
  opts = {},
) {
  const { debug = false } = opts;
  const { wpGraphql } = await import("./wpGraphql");

  const p = String(purpose || "")
    .trim()
    .toLowerCase();
  const l = String(lang || "ZH").trim();

  if (!p) throw new Error("Missing purpose");
  if (!OTP_PURPOSES.has(p)) throw new Error("Invalid purpose");

  const stored = wpGetTokens();
  const authToken = stripBearer(stored?.authToken || "");
  const tokenToUse = otpPurposeNeedsAuth(p) ? authToken : "";

  let e = String(email || "").trim();

  if (otpPurposeUsesMeEmail(p)) {
    if (!e) e = await getAuthedUserEmail({ debug });
    if (!e)
      throw new Error("Missing email (user has no email or not logged in)");
  } else {
    if (!e) throw new Error("Missing email");
  }

  const data = await wpGraphql(
    REQUEST_EMAIL_OTP_MUTATION,
    { purpose: p, email: e || null, lang: l },
    {
      token: tokenToUse,
      requestTag: "requestEmailOtp",
      operationName: "RequestEmailOtp",
      retryOnAuth: false,
      debug,
    },
  );

  const payload = data?.sehoRequestEmailOtp || null;
  if (!payload?.ok) {
    const msg = String(payload?.message || "Failed to request OTP");
    throw new Error(msg);
  }

  return payload;
}

export async function wpVerifyEmailOtp({ purpose, email, code }, opts = {}) {
  const { debug = false } = opts;
  const { wpGraphql } = await import("./wpGraphql");

  const p = String(purpose || "")
    .trim()
    .toLowerCase();
  const c = String(code || "").trim();

  if (!p) throw new Error("Missing purpose");
  if (!OTP_PURPOSES.has(p)) throw new Error("Invalid purpose");
  if (!c) throw new Error("Missing code");

  const stored = wpGetTokens();
  const authToken = stripBearer(stored?.authToken || "");
  const tokenToUse = otpPurposeNeedsAuth(p) ? authToken : "";

  let e = String(email || "").trim();

  if (otpPurposeUsesMeEmail(p)) {
    if (!e) e = await getAuthedUserEmail({ debug });
    if (!e)
      throw new Error("Missing email (user has no email or not logged in)");
  } else {
    if (!e) throw new Error("Missing email");
  }

  const data = await wpGraphql(
    VERIFY_EMAIL_OTP_MUTATION,
    { purpose: p, email: e || null, code: c },
    {
      token: tokenToUse,
      requestTag: "verifyEmailOtp",
      operationName: "VerifyEmailOtp",
      retryOnAuth: false,
      debug,
    },
  );

  const payload = data?.sehoVerifyEmailOtp || null;
  if (!payload?.ok) {
    const msg = String(payload?.message || "OTP verification failed");
    throw new Error(msg);
  }

  return payload;
}

/* =========================================================
   ✅ NEW: Forgot / Reset public APIs
========================================================= */

export async function wpForgotUsername({ email, lang = "ZH" }, opts = {}) {
  const { debug = false } = opts;
  const { wpGraphql } = await import("./wpGraphql");

  const e = String(email || "").trim();
  if (!e) throw new Error("Missing email");

  const l = String(lang || "ZH").trim();

  const data = await wpGraphql(
    FORGOT_USERNAME_MUTATION,
    { email: e, lang: l },
    {
      token: "",
      requestTag: "forgotUsername",
      operationName: "ForgotUsername",
      retryOnAuth: false,
      debug,
    },
  );

  const payload = data?.sehoForgotUsername || null;
  if (!payload) throw new Error("Invalid response");
  // Note: backend may return ok=false but not throw; keep UI-friendly behavior:
  return payload; // { ok, message }
}

export async function wpForgotPassword({ email, lang = "ZH" }, opts = {}) {
  const { debug = false } = opts;
  const { wpGraphql } = await import("./wpGraphql");

  const e = String(email || "").trim();
  if (!e) throw new Error("Missing email");

  const l = String(lang || "ZH").trim();

  const data = await wpGraphql(
    FORGOT_PASSWORD_MUTATION,
    { email: e, lang: l },
    {
      token: "",
      requestTag: "forgotPassword",
      operationName: "ForgotPassword",
      retryOnAuth: false,
      debug,
    },
  );

  const payload = data?.sehoForgotPassword || null;
  if (!payload) throw new Error("Invalid response");
  return payload; // { ok, message, expiresIn }
}

/**
 * ✅ NEW:
 * Validate reset password token WITHOUT consuming it.
 * Use this on ResetPasswordPage mount:
 * - if ok=false => redirect to NotFound
 */
export async function wpValidateResetPasswordToken({ token }, opts = {}) {
  const { debug = false } = opts;
  const { wpGraphql } = await import("./wpGraphql");

  const t = String(token || "").trim();
  if (!t) throw new Error("Missing token");

  const data = await wpGraphql(
    VALIDATE_RESET_PASSWORD_TOKEN_MUTATION,
    { token: t },
    {
      token: "",
      requestTag: "validateResetToken",
      operationName: "ValidateResetPasswordToken",
      retryOnAuth: false,
      debug,
    },
  );

  const payload = data?.sehoValidateResetPasswordToken || null;
  if (!payload) throw new Error("Invalid response");

  return payload; // { ok, message }
}

export async function wpResetPasswordWithToken(
  { token, newPassword },
  opts = {},
) {
  const { debug = false } = opts;
  const { wpGraphql } = await import("./wpGraphql");

  const t = String(token || "").trim();
  const np = String(newPassword || "");

  if (!t) throw new Error("Missing token");
  if (!np) throw new Error("Missing new password");
  if (np.length < 8) throw new Error("Password must be at least 8 characters");

  const data = await wpGraphql(
    RESET_PASSWORD_WITH_TOKEN_MUTATION,
    { token: t, newPassword: np },
    {
      token: "",
      requestTag: "resetPassword",
      operationName: "ResetPasswordWithToken",
      retryOnAuth: false,
      debug,
    },
  );

  const payload = data?.sehoResetPasswordWithToken || null;
  if (!payload) throw new Error("Invalid response");

  // IMPORTANT: do not auto-login here; just return result
  return payload; // { ok, message }
}

/* =========================================================
   Register / Update profile helpers (existing)
========================================================= */

export async function wpRegisterCustomer(
  {
    username,
    email,
    password,
    firstName,
    lastName,
    phoneNumber,
    keepLogin = false,
    emailOtp, // register OTP
    birthday,
    birthdate,
    gender,
    emailOtpForEditProfile,
  },
  opts = {},
) {
  const { debug = false } = opts;
  const { wpGraphql } = await import("./wpGraphql");

  const b = normalizeBirthday(birthday || birthdate);
  const g = normalizeGender(gender);

  const data = await wpGraphql(
    REGISTER_MUTATION,
    {
      username,
      email: email || null,
      password,
      firstName: firstName || null,
      lastName: lastName || null,
      phoneNumber: phoneNumber || null,
      keepLogin: !!keepLogin,
      emailOtp: emailOtp || null,
    },
    {
      token: "",
      requestTag: "register",
      operationName: "Register",
      retryOnAuth: false,
      debug,
      allowRegisterSideEffect: true,
    },
  );

  const payload = data?.sehoRegisterCustomer || null;
  if (!payload?.ok) {
    const msg = String(payload?.message || "Registration failed");
    throw new Error(msg);
  }

  if (!b && !g) return payload;

  const tok = stripBearer(safeGet(AUTH_KEY));

  if (!tok) {
    return {
      ...payload,
      __needsProfileUpdateAfterLogin: true,
      __pendingProfile: { birthday: b || "", gender: g || "" },
    };
  }

  const otp = String(emailOtpForEditProfile || "").trim();
  if (!otp) {
    return {
      ...payload,
      __needsProfileUpdateAfterLogin: true,
      __pendingProfile: { birthday: b || "", gender: g || "" },
    };
  }

  const data2 = await wpGraphql(
    UPDATE_PROFILE_MUTATION,
    {
      email: null,
      firstName: null,
      lastName: null,
      phoneNumber: null,
      landlinePhoneNumber: null,
      address: null,
      birthday: b || null,
      gender: g || null,
      emailOtp: otp,
    },
    {
      token: tok,
      requestTag: "updateMyProfile",
      operationName: "UpdateMyProfile",
      retryOnAuth: true,
      debug,
    },
  );

  const payload2 = data2?.sehoUpdateMyProfile || null;
  if (payload2?.ok) {
    try {
      await wpFetchMe({ debug });
    } catch (_) {}
  }

  return {
    ...payload,
    __profileUpdated: !!payload2?.ok,
  };
}

export async function wpUpdateMyProfile(
  {
    firstName,
    lastName,
    phoneNumber,
    landlinePhoneNumber,
    address,
    birthday,
    birthdate,
    gender,
    emailOtp,
  },
  opts = {},
) {
  const { debug = false } = opts;
  const { wpGraphql } = await import("./wpGraphql");

  const tok = stripBearer(safeGet(AUTH_KEY));

  if (!tok) {
    throw new Error("Not logged in");
  }

  const b = normalizeBirthday(birthday || birthdate);
  const g = normalizeGender(gender);

  const data = await wpGraphql(
    UPDATE_PROFILE_MUTATION,
    {
      email: null,
      firstName: firstName ?? null,
      lastName: lastName ?? null,
      phoneNumber: phoneNumber ?? null,
      landlinePhoneNumber:
        landlinePhoneNumber !== undefined ? String(landlinePhoneNumber) : null,
      address: address !== undefined ? String(address) : null,
      birthday: b || null,
      gender: g || null,
      emailOtp: emailOtp || null,
    },
    {
      token: tok,
      requestTag: "updateMyProfile",
      operationName: "UpdateMyProfile",
      retryOnAuth: true,
      debug,
    },
  );

  const payload = data?.sehoUpdateMyProfile || null;

  if (!payload?.ok) {
    throw new Error(String(payload?.message || "Update profile failed"));
  }

  try {
    await wpFetchMe({ debug });
  } catch (_) {
    // The mutation result is still valid if refreshing the cache fails.
  }

  return payload;
}

/* =========================================================
   Login / Refresh (existing)
========================================================= */

export async function wpLogin(
  username,
  password,
  opts = {},
) {
  const {
    debug = false,
    keepLogin = false,
  } = opts;

  const { wpGraphql } =
    await import("./wpGraphql");

  const normalizedUsername =
    String(username || "").trim();

  const normalizedPassword =
    String(password || "");

  const selectedKeepLogin =
    Boolean(keepLogin);

  if (!normalizedUsername) {
    throw new Error(
      "Missing username",
    );
  }

  if (!normalizedPassword) {
    throw new Error(
      "Missing password",
    );
  }

  /*
   * Create a new frontend operation identity.
   *
   * Any earlier login or refresh operation becomes stale immediately.
   */
  const loginGeneration =
    ++authSessionGeneration;

  /*
   * Every login starts clean.
   *
   * Previous tokens, previous session information, and the previous
   * 保持登入 mode are removed before requesting new tokens.
   */
  clearLocalAuthSessionStorage();

  notifyAuthChanged({
    user: null,
    hasToken: false,
    logout: false,
    reason: "login_start",
  });

  try {
    const data = await wpGraphql(
      LOGIN_MUTATION,
      {
        username:
          normalizedUsername,

        password:
          normalizedPassword,

        keepLogin:
          selectedKeepLogin,
      },
      {
        token: "",
        requestTag: "login",
        operationName:
          "SehoLoginCustomer",
        retryOnAuth: false,
        debug,
      },
    );

    /*
     * Another login or logout occurred while this request was running.
     * This response must not modify the newer browser session.
     */
    if (
      loginGeneration !==
      authSessionGeneration
    ) {
      throw new Error(
        "Login was replaced by a newer authentication action",
      );
    }

    const payload =
      data?.sehoLoginCustomer ||
      null;

    if (!payload?.ok) {
      throw new Error(
        String(
          payload?.message ||
            "Login failed",
        ),
      );
    }

    /*
     * Verify that:
     * - both tokens contain signed SEHO session claims;
     * - both belong to the same browser session;
     * - the deadline matches the backend response;
     * - the mode matches the checkbox selected for this login.
     */
    const validated =
      validateNewAuthTokenPair({
        authToken:
          payload.authToken,

        refreshToken:
          payload.refreshToken,

        expectedSessionId:
          payload.sessionId,

        expectedSessionExpiresAt:
          payload.sessionExpiresAt,

        expectedKeepLogin:
          payload.keepLogin,
      });

    if (
      validated.keepLogin !==
      selectedKeepLogin
    ) {
      throw new Error(
        "Login session does not match the selected keep-login mode",
      );
    }

    if (
      loginGeneration !==
      authSessionGeneration
    ) {
      throw new Error(
        "Login was replaced by a newer authentication action",
      );
    }

    /*
     * Store the complete session only after the response and both
     * tokens have passed validation.
     */
    const savedSession =
      saveLocalAuthSession({
        sessionId:
          validated.sessionId,

        sessionExpiresAt:
          validated.expiresAtMs,

        keepLogin:
          validated.keepLogin,
      });

    safeSet(
      AUTH_KEY,
      validated.authToken,
    );

    safeSet(
      REFRESH_KEY,
      validated.refreshToken,
    );

    /*
     * Confirm localStorage actually contains the complete session.
     * The safe storage helpers intentionally do not throw browser
     * storage errors, so the stored values must be checked here.
     */
    const storedSession =
      wpGetAuthSession();

    const storedAuthToken =
      stripBearer(
        safeGet(AUTH_KEY),
      );

    const storedRefreshToken =
      stripBearer(
        safeGet(REFRESH_KEY),
      );

    if (
      !storedSession ||
      storedSession.sessionId !==
        savedSession.sessionId ||
      storedSession.expiresAtMs !==
        savedSession.expiresAtMs ||
      storedSession.keepLogin !==
        savedSession.keepLogin ||
      storedAuthToken !==
        validated.authToken ||
      storedRefreshToken !==
        validated.refreshToken
    ) {
      throw new Error(
        "Unable to save the authentication session",
      );
    }

    /*
     * Confirm the new access token by requesting viewer directly.
     *
     * retryOnAuth is disabled during login so a failed confirmation
     * cannot start a refresh/logout loop or display an expired-token
     * dialog immediately after login.
     */
    const meData =
      await wpGraphql(
        ME_QUERY,
        {},
        {
          token:
            validated.authToken,

          requestTag:
            "loginMe",

          operationName:
            "Me",

          retryOnAuth: false,
          debug,
        },
      );

    if (
      loginGeneration !==
        authSessionGeneration ||
      !wpIsCurrentAuthSession(
        validated.sessionId,
      )
    ) {
      throw new Error(
        "Login was replaced by a newer authentication action",
      );
    }

    const me =
      meData?.viewer || null;

    if (!me?.id) {
      throw new Error(
        "Unable to confirm the logged-in user",
      );
    }

    const cachedUser =
      compactUserForCache(me);

    safeSetJson(
      USER_KEY,
      cachedUser,
    );

    notifyAuthChanged({
      user: cachedUser,
      hasToken: true,

      expMs:
        validated.expiresAtMs,

      sessionId:
        validated.sessionId,

      keepLogin:
        validated.keepLogin,

      reason: "login",
    });

    return {
      login: payload,
      me: cachedUser,
    };
  } catch (error) {
    /*
     * Clear storage only if this is still the newest authentication
     * operation. An older failed login must not clear a newer login.
     */
    if (
      loginGeneration ===
      authSessionGeneration
    ) {
      clearLocalAuthSessionStorage();

      notifyAuthChanged({
        user: null,
        hasToken: false,
        logout: false,
        reason: "login_failed",
      });
    }

    throw error;
  }
}

export async function wpLoginByIdentifier(
  { identifier, password, cc = "+886" },
  opts = {},
) {
  const id = String(identifier || "").trim();
  if (!id) throw new Error("missing identifier");
  if (!password) throw new Error("missing password");

  const nextOpts = { ...opts, keepLogin: !!opts?.keepLogin };

  function isTWLocal(p) {
    return /^09\d{8}$/.test(String(p || "").replace(/\s|-/g, ""));
  }

  function toLocalTW(cc2, num) {
    const n = String(num || "").replace(/\s|-/g, "");
    if (cc2 === "+886") {
      if (/^9\d{8}$/.test(n)) return "0" + n;
      if (/^09\d{8}$/.test(n)) return n;
    }
    return null;
  }

  const asPhoneLocal = isTWLocal(id) ? id : toLocalTW(cc, id) || null;
  const primary = asPhoneLocal || id;

  try {
    return await wpLogin(primary, password, nextOpts);
  } catch (e) {
    const m = String(e?.message || "").toLowerCase();
    const looksUserNotFound =
      m.includes("invalid username") ||
      m.includes("unknown user") ||
      m.includes("user does not exist") ||
      m.includes("no user");

    if (asPhoneLocal && looksUserNotFound) {
      return await wpLogin(`p_${asPhoneLocal}`, password, nextOpts);
    }
    throw e;
  }
}

export async function wpRefreshAuthToken(
  opts = {},
) {
  const {
    debug = false,
    skipMe = false,
  } = opts;

  const { wpGraphql } =
    await import("./wpGraphql");

  /*
   * Capture the exact browser session that started this refresh.
   *
   * The result may be saved only while all of these values still
   * belong to the current session.
   */
  const refreshGeneration =
    authSessionGeneration;

  const sessionAtStart =
    wpGetAuthSession();

  const {
    refreshToken,
  } = wpGetTokens();

  const refreshTokenAtStart =
    stripBearer(refreshToken);

  if (!sessionAtStart) {
    throw new Error(
      "Missing authentication session. Please login again.",
    );
  }

  if (
    wpIsAuthSessionExpired(
      sessionAtStart,
    )
  ) {
    throw new Error(
      "Authentication session is expired. Please login again.",
    );
  }

  if (!refreshTokenAtStart) {
    throw new Error(
      "Missing refreshToken. Please login again.",
    );
  }

  const data = await wpGraphql(
    REFRESH_MUTATION,
    {
      refreshToken:
        refreshTokenAtStart,
    },
    {
      token: "",
      requestTag: "refresh",
      operationName:
        "RefreshAuthToken",
      retryOnAuth: false,
      debug,
    },
  );

  /*
   * A login or logout occurred while the refresh request was running.
   * The old response must not modify the newer browser session.
   */
  if (
    refreshGeneration !==
      authSessionGeneration ||
    !wpIsCurrentAuthSession(
      sessionAtStart.sessionId,
    )
  ) {
    throw new Error(
      "Token refresh belongs to an older authentication session",
    );
  }

  /*
   * The refresh token must also still be the same one that started
   * this operation.
   */
  const currentRefreshToken =
    stripBearer(
      wpGetTokens().refreshToken,
    );

  if (
    currentRefreshToken !==
    refreshTokenAtStart
  ) {
    throw new Error(
      "Token refresh was replaced by a newer authentication session",
    );
  }

  const returnedAuthToken =
    stripBearer(
      data?.refreshJwtAuthToken
        ?.authToken || "",
    );

  if (!returnedAuthToken) {
    throw new Error(
      "Failed to refresh auth token. Please login again.",
    );
  }

  /*
   * Confirm that the refreshed access token retains:
   * - the same browser-session ID;
   * - the same fixed 24-hour or 31-day deadline;
   * - the same 保持登入 mode.
   */
  const newAuthToken =
    validateRefreshedAuthToken(
      returnedAuthToken,
      sessionAtStart,
    );

  if (
    refreshGeneration !==
      authSessionGeneration ||
    !wpIsCurrentAuthSession(
      sessionAtStart.sessionId,
    ) ||
    stripBearer(
      wpGetTokens().refreshToken,
    ) !== refreshTokenAtStart
  ) {
    throw new Error(
      "Token refresh belongs to an older authentication session",
    );
  }

  /*
   * Replace only the access token.
   *
   * The existing refresh token and fixed browser-session information
   * remain unchanged.
   */
  safeSet(
    AUTH_KEY,
    newAuthToken,
  );

  if (
    stripBearer(
      safeGet(AUTH_KEY),
    ) !== newAuthToken
  ) {
    throw new Error(
      "Unable to save the refreshed authentication token",
    );
  }

  notifyAuthChanged({
    hasToken: true,
    expMs:
      sessionAtStart.expiresAtMs,

    sessionId:
      sessionAtStart.sessionId,

    keepLogin:
      sessionAtStart.keepLogin,

    reason: "refresh",
  });

  /*
   * Refreshing the cached viewer remains best-effort.
   *
   * The result is cached only if the same browser session is still
   * active when the request finishes.
   */
  if (!skipMe) {
    try {
      const meData =
        await wpGraphql(
          ME_QUERY,
          {},
          {
            token: newAuthToken,
            requestTag:
              "refreshMe",
            operationName: "Me",
            retryOnAuth: false,
            debug,
          },
        );

      if (
        refreshGeneration ===
          authSessionGeneration &&
        wpIsCurrentAuthSession(
          sessionAtStart.sessionId,
        )
      ) {
        const viewer =
          meData?.viewer || null;

        if (viewer?.id) {
          safeSetJson(
            USER_KEY,
            compactUserForCache(
              viewer,
            ),
          );
        }
      }
    } catch (error) {
      if (debug) {
        console.warn(
          "[wpRefreshAuthToken] viewer refresh failed:",
          error,
        );
      }
    }
  }

  return newAuthToken;
}

export function wpOnAuthChanged(handler) {
  if (typeof window === "undefined") return () => {};
  const fn = (e) => handler?.(e?.detail || {});
  window.addEventListener(AUTH_EVENT, fn);
  return () => window.removeEventListener(AUTH_EVENT, fn);
}

export const WP_AUTH_EVENT =
  AUTH_EVENT;

export const WP_AUTH_STORAGE_KEYS = {
  AUTH_KEY,
  REFRESH_KEY,
  USER_KEY,
  SESSION_ID_KEY,
  SESSION_EXPIRES_AT_KEY,
  SESSION_KEEP_LOGIN_KEY,
};
